package dev.lingvo.site.components.editor

import androidx.compose.runtime.Composable
import dev.lingvo.shared.QaCheck
import dev.lingvo.shared.QaIssueDto
import dev.lingvo.shared.QaSeverity
import dev.lingvo.site.ds.LArrowUpDownIcon
import dev.lingvo.site.ds.LBookOpenIcon
import dev.lingvo.site.ds.LBracesIcon
import dev.lingvo.site.ds.LCircleXIcon
import dev.lingvo.site.ds.LCommentIcon
import dev.lingvo.site.ds.LCopyIcon
import dev.lingvo.site.ds.LListChecksIcon
import dev.lingvo.site.ds.LSquarePenIcon
import dev.lingvo.site.ds.LTagIcon
import dev.lingvo.site.i18n.tr

/**
 * How a QA check looks and where it sorts, kept apart from what it means.
 *
 * `QaCheck.displayName()` lives in `EditorModels.kt` beside the rest of the editor's vocabulary, and
 * that file is not this family's. Everything a *surface* decides about a check — its glyph, the
 * order its group appears in, the word for its severity — belongs together and belongs here, because
 * all three are read by the QA page, the group headers and the readiness row and all three have to
 * agree.
 *
 * **The glyph never replaces the name.** Nine checks is more than anybody memorises as pictures, and
 * a group header that said only "⌗" would be unreadable in the one place a release is blocked. Every
 * icon returned here is drawn `aria-hidden` beside the check's written name.
 */
internal fun QaCheck.icon(): @Composable () -> Unit = when (this) {
    // Nothing is there, which is the one check whose glyph can be literal.
    QaCheck.EMPTY -> ({ LCircleXIcon() })
    QaCheck.PLACEHOLDER_MISMATCH -> ({ LBracesIcon() })
    QaCheck.MISSING_PLURAL_FORM -> ({ LListChecksIcon() })
    QaCheck.LENGTH_EXCEEDED -> ({ LArrowUpDownIcon() })
    QaCheck.LEADING_TRAILING_WHITESPACE -> ({ LSquarePenIcon() })
    QaCheck.SAME_AS_SOURCE -> ({ LCopyIcon() })
    QaCheck.TAG_MISMATCH -> ({ LTagIcon() })
    QaCheck.GLOSSARY_VIOLATION -> ({ LBookOpenIcon() })
    QaCheck.WRONG_QUOTE_STYLE -> ({ LCommentIcon() })
}

/**
 * One check's findings, sized by the server rather than by what the page drew.
 *
 * [total] is `QaSummaryDto.countsByCheck` when the page has a summary and the rendered count when it
 * does not. The difference matters at the 1,000-issue cap: a group header reading "200 warnings"
 * over a truncated list tells a release manager the check is nearly settled when it is not.
 */
internal class QaGroup(
    val check: QaCheck,
    val issues: List<QaIssueDto>,
    val total: Int,
) {
    val errors: Int = issues.count { it.severity == QaSeverity.ERROR }
    val warnings: Int = issues.size - errors

    /** Nothing here blocks a release, so the group opens collapsed. */
    val advisoryOnly: Boolean get() = errors == 0
}

/**
 * The groups, worst first.
 *
 * Spec §8: sorted by (has errors, error count, total). A release manager opens this page to find
 * what is blocking them; enum declaration order put "Empty translations" above a check with forty
 * errors because `EMPTY` happens to be declared first.
 *
 * @param countsByCheck `QaSummaryDto.countsByCheck`, keyed by [QaCheck.name]. Empty when the caller
 *   has only the issue list, in which case the rendered size is the honest total.
 */
internal fun qaGroups(
    issues: List<QaIssueDto>,
    countsByCheck: Map<String, Int> = emptyMap(),
    renderCap: Int,
): List<QaGroup> {
    val byCheck = issues.groupBy(QaIssueDto::check)
    return QaCheck.entries
        .mapNotNull { check ->
            val found = byCheck[check].orEmpty()
            if (found.isEmpty()) return@mapNotNull null
            val ordered = found.sortedWith(
                compareBy<QaIssueDto> { if (it.severity == QaSeverity.ERROR) 0 else 1 }
                    .thenBy { it.keyName.lowercase() }
                    .thenBy(QaIssueDto::locale),
            )
            QaGroup(
                check = check,
                issues = ordered.take(renderCap),
                // Never smaller than what is on screen: a server count that predates a filter would
                // otherwise make the header claim fewer findings than the rows under it.
                total = maxOf(countsByCheck[check.name] ?: found.size, found.size),
            )
        }
        .sortedWith(
            compareBy<QaGroup> { if (it.errors > 0) 0 else 1 }
                .thenByDescending(QaGroup::errors)
                .thenByDescending(QaGroup::total)
                .thenBy { it.check.ordinal },
        )
}

/** The severity as a word, never `name.lowercase()` — it is announced and it is translated. */
internal fun QaSeverity.severityWord(): String = when (this) {
    QaSeverity.ERROR -> tr("editor.qa_presentation.error", "Error")
    QaSeverity.WARNING -> tr("editor.qa_presentation.warning", "Warning")
}
